import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { CloudUploadSmall } from "components/icons/CloudUploadSmall";
import { PlusIcon } from "components/icons/PlusIcon";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { parse, stringify } from "qs";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { useAddBillOrRequestMutation } from "store/apis/vendorBills";
import { BackendError } from "types/utils/error";
import { BulkBillUpload } from "./BulkBillUpload";
import { UploadBillFromPDF } from "./UploadBillFromPDF";
import { Import } from "components/icons/Import";
import { MagicWand } from "components/icons/MagicWand";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import UploadSimple from "static/images/UploadSimple.svg";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import { colors } from "constants/colors";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useGetGroupDataQuery } from "store/apis/group";

export const AddRequestAndBill = () => {
  const { alertToast } = useToast();
  const bulkUploadModal = useModal();
  const pdfUploadModal = useModal();
  const history = useHistory();
  const [createUpdateDraft] = useCreateMessageDraftMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const location = useLocation();
  const { path } = useRouteMatch();
  const entityId = useCurrentEntityId();
  const { isAdmin } = useRoleBasedView();
  const { data: groupData } = useGetGroupDataQuery(
    { groupId: groupId! },
    { skip: !groupId || !isAdmin }
  );

  const parsedSearch = parse(location.search, { ignoreQueryPrefix: true });
  const [addBillOrRequest, { isLoading: isAdding, originalArgs }] =
    useAddBillOrRequestMutation();

  const searchQuery = stringify(
    {
      entity: parsedSearch.entity,
      company: parsedSearch.company,
    },
    { skipNulls: true, addQueryPrefix: true }
  );

  const handleAddBillOrRequest = async ({
    billType,
  }: {
    billType: keyof typeof FINANCIAL_RECORD_TYPE;
  }) => {
    try {
      const { uuid } = await addBillOrRequest({
        bill_type: billType,
        entityId,
        groupId,
      }).unwrap();
      if (billType === FINANCIAL_RECORD_TYPE.BILL) {
        history.push(`${path}/bill/${uuid}${searchQuery}`);
        return;
      }
      if (billType === FINANCIAL_RECORD_TYPE.REQUEST) {
        history.push(`${path}/request/${uuid}${searchQuery}`);
        return;
      }
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const isAddingBill =
    originalArgs?.bill_type === FINANCIAL_RECORD_TYPE.BILL && isAdding;

  const handleSmartCreateBill = () => {
    const aiChannelId = groupData?.ai_channel_url;
    const chatPath = aiChannelId
      ? `/admin/chat/${aiChannelId}?ai=true`
      : "/admin/chat/?ai=true";
    if (aiChannelId) {
      createUpdateDraft({
        channelUrl: aiChannelId,
        message: "Create a bill",
      });
    }
    history.push(chatPath);
  };

  const handleUploadBillFromDocument = () => {
    const aiChannelId = groupData?.ai_channel_url;
    const chatPath = aiChannelId
      ? `/admin/chat/${aiChannelId}?ai=true`
      : "/admin/chat/?ai=true";
    if (aiChannelId) {
      createUpdateDraft({
        channelUrl: aiChannelId,
        message: "Create bill from uploaded document",
      });
    }
    history.push(chatPath);
  };

  return (
    <>
      <div className="t-flex t-justify-between t-gap-2">
        <Dropdown.Root>
          <Dropdown.Trigger asChild className="t-group t-text-text-60">
            <div>
              <Button
                isLoading={isAddingBill}
                disabled={isAddingBill}
                size="xsmall"
                customType="primary"
              >
                Add bill
                <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface t-ml-1.5">
                  <ArrowDown color="currentColor" strokeWidth="1.5" />
                </div>
              </Button>
            </div>
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Content
              sideOffset={8}
              side="bottom"
              className="t-min-w-48"
              align="end"
            >
              {isAdmin && (
                <>
                  <Dropdown.Item
                    className="t-px-3 t-py-2 t-w-full"
                    onSelect={handleSmartCreateBill}
                    icon={<MagicWand color={colors.text.black} />}
                  >
                    <div className="t-flex t-items-center t-justify-between t-w-full">
                      <div className="t-flex t-items-center t-gap-2">
                        <span>Smart create</span>
                      </div>

                      <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-body-sm t-px-2 t-rounded-full">
                        <img
                          src={AIGenerateButtonStar}
                          alt="AI star"
                          className="t-w-3 t-h-3"
                        />
                        AI
                      </div>
                    </div>
                  </Dropdown.Item>

                  <Dropdown.Item
                    className="t-px-3 t-py-2 t-w-full"
                    onSelect={handleUploadBillFromDocument}
                    icon={
                      <img
                        src={UploadSimple}
                        alt="UploadSimple"
                        className="t-size-4"
                      />
                    }
                  >
                    <div className="t-flex t-items-center t-justify-between t-w-full">
                      <div className="t-flex t-items-center t-gap-2">
                        <span>Upload file</span>
                      </div>

                      <div className="t-flex t-items-center t-gap-1 t-bg-purple-0 t-text-purple-600 t-text-xs t-px-2 t-text-body-sm t-rounded-full">
                        <img
                          src={AIGenerateButtonStar}
                          alt="AI star"
                          className="t-w-3 t-h-3"
                        />
                        AI
                      </div>
                    </div>
                  </Dropdown.Item>
                </>
              )}
              <Dropdown.Item
                onSelect={() =>
                  handleAddBillOrRequest({
                    billType: FINANCIAL_RECORD_TYPE.BILL,
                  })
                }
              >
                <div className="t-flex t-items-center t-gap-2">
                  <span className="t-text-text-30">
                    <PlusIcon size="12" />
                  </span>
                  <div>Create manually</div>
                </div>
              </Dropdown.Item>
              <Dropdown.Item onSelect={bulkUploadModal.open}>
                <div className="t-flex t-items-center t-gap-2">
                  <span className="t-flex t-text-text-30">
                    <Import />
                  </span>
                  <div>Import .xls/.xlsx file</div>
                </div>
              </Dropdown.Item>
              <Dropdown.Item onSelect={pdfUploadModal.open}>
                <div className="t-flex t-items-center t-gap-2">
                  <CloudUploadSmall size="14" />
                  <div>Upload PDF/Image</div>
                </div>
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>
      <BulkBillUpload {...bulkUploadModal} />
      <UploadBillFromPDF {...pdfUploadModal} />
    </>
  );
};
